import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UserAuthenticatedLayout } from "@/layouts/AuthenticatedLayout/UserAuthenticatedLayout";
import { FirstLoadingPageView } from "@/modules/theme/components/FirstLoadingPageView";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { ParsedTutorial } from "@/modules/tutorials/types/ParsedTutorial";

import { EmptyTutorialsView } from "./components/EmptyTutorialsView";
import { TutorialItem } from "./components/TutorialItem.tsx/TutorialItem";
import { createTutorialsPageStyles } from "./helpers/createTutorialsPageStyles";
import { useGetUnlockedTutorialsQuery } from "./hooks/useGetUnlockedTutorialsQuery";

const translationNamespace = "pages/TutorialsPage";

export const TutorialsPage = () => {
  const { t } = useTranslation(translationNamespace);
  const classes = createTutorialsPageStyles(useTheme());

  const {
    data: unlockedTutorials,
    isFetching: areUnlockedTutorialsFetching,
  } = useGetUnlockedTutorialsQuery();

  const isTutorialsListEmpty =
    !unlockedTutorials?.length && !areUnlockedTutorialsFetching;

  const groupedUnlockedTutorials = useMemo(() => {
    return unlockedTutorials?.reduce(
      (group: Record<number, Array<ParsedTutorial>>, tutorial) => {
        const { minimumLevelOrder } = tutorial;
        group[minimumLevelOrder] = group[minimumLevelOrder] ?? [];
        group[minimumLevelOrder].push(tutorial);
        return group;
      },
      {}
    );
  }, [unlockedTutorials]);

  if (areUnlockedTutorialsFetching) {
    return <FirstLoadingPageView heading={t("Tutorials")} />;
  }

  if (isTutorialsListEmpty) {
    return <EmptyTutorialsView />;
  }

  return (
    <UserAuthenticatedLayout containerClassName={classes.root}>
      <PageMeta title={t("Tutorials")} />
      <Typography variant="pageHeading" className={classes.heading}>
        {t("Tutorials")}
      </Typography>
      {areUnlockedTutorialsFetching ? (
        <div className={classes.loaderWrapper}>
          <Loader size={70} variant="primary" />
        </div>
      ) : (
        groupedUnlockedTutorials &&
        Object.entries(groupedUnlockedTutorials).map(([level, tutorials]) => (
          <div className={classes.levelTutorialsWrapper} key={level}>
            {tutorials.map((tutorial) => (
              <TutorialItem tutorial={tutorial} key={tutorial.id} />
            ))}
          </div>
        ))
      )}
    </UserAuthenticatedLayout>
  );
};
